import type {
  InstalledPlugin,
  PluginSettingsSnapshot,
  PluginSettingValue,
} from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import { InlineMessage, LoadingState } from '@crowdex/ui/components/feedback';
import { Input } from '@crowdex/ui/components/input';
import { NativeSelect } from '@crowdex/ui/components/native-select';
import { PendingButton } from '@crowdex/ui/components/pending-button';
import { Slider } from '@crowdex/ui/components/slider';
import { Switch } from '@crowdex/ui/components/switch';
import { useEffect, useId, useState } from 'react';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';
import { ModelSettingPicker, VoiceSettingPicker } from './plugin-setting-pickers';

export function PluginSettingsForm({
  plugin,
  workspaceRoot,
}: {
  plugin: InstalledPlugin;
  workspaceRoot?: string | undefined;
}) {
  const scopeId = useId();
  const hasSettings = Object.keys(plugin.settings ?? {}).length > 0;
  const [project, setProject] = useState<string | undefined>();
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!hasSettings) return;
    let active = true;
    void nativePluginClient.settingsProjects(workspaceRoot).then(
      (value) => {
        if (active) setProjects(value);
      },
      (reason) => {
        if (active) setError(String(reason));
      },
    );
    return () => {
      active = false;
    };
  }, [workspaceRoot, hasSettings]);
  if (!hasSettings) return null;
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-medium text-sm">Preferences</h3>
        {projects.length > 0 && (
          <label
            htmlFor={scopeId}
            className="flex items-center gap-2 text-muted-foreground text-xs"
          >
            For
            <NativeSelect
              id={scopeId}
              className="h-control-sm w-48 text-xs"
              value={project ?? ''}
              onChange={(event) => setProject(event.target.value || undefined)}
            >
              <option value="">All projects</option>
              {projects.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </NativeSelect>
          </label>
        )}
      </div>
      {error && <InlineMessage tone="error">{error}</InlineMessage>}
      <SettingsFields
        key={`${plugin.name}:${project ?? ''}`}
        plugin={plugin}
        project={project}
        workspaceRoot={workspaceRoot}
      />
    </section>
  );
}
function SettingsFields({
  plugin,
  project,
  workspaceRoot,
}: {
  plugin: InstalledPlugin;
  project: string | undefined;
  workspaceRoot: string | undefined;
}) {
  const [snapshot, setSnapshot] = useState<PluginSettingsSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(
    () =>
      nativePluginClient.subscribeSettings(
        plugin.name,
        (value) => {
          setSnapshot(value);
          setError(null);
        },
        {
          ...(project ? { project } : {}),
          ...(workspaceRoot ? { workspaceRoot } : {}),
          onError: (reason) => setError(String(reason)),
        },
      ),
    [plugin.name, project, workspaceRoot],
  );
  async function save(key: string, value: PluginSettingValue | null) {
    setBusy(true);
    setError(null);
    try {
      setSnapshot(
        await nativePluginClient.updateSettings(
          plugin.name,
          { [key]: value },
          project,
          workspaceRoot,
        ),
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }
  const entries = Object.entries(plugin.settings ?? {}).filter(
    ([, field]) => !project || field.scope === 'project',
  );
  return (
    <div className="space-y-2">
      {error && <InlineMessage tone="error">{error}</InlineMessage>}
      {!snapshot && <LoadingState>Loading preferences…</LoadingState>}
      {snapshot && entries.length === 0 && (
        <p className="text-muted-foreground text-xs">No preference can be set per project.</p>
      )}
      {snapshot && entries.length > 0 && (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
          {entries.map(([key, field]) => {
            const value = snapshot.values[key] ?? field.default;
            const id = `setting-${plugin.name}-${key}`;
            const inline =
              field.type === 'boolean' ||
              field.choices !== undefined ||
              field.picker !== undefined ||
              slider(field);
            const source = snapshot.sources[key];
            const control = (
              <SettingInput
                key={JSON.stringify(value)}
                id={id}
                describedBy={`${id}-description`}
                label={field.title}
                field={field}
                value={value}
                values={snapshot.values}
                busy={busy}
                save={(next) => void save(key, next)}
              />
            );
            return (
              <div key={key} className="bg-background/55 px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <label className="block text-sm" htmlFor={id}>
                      {field.title}
                    </label>
                    <p className="mt-0.5 text-muted-foreground text-xs" id={`${id}-description`}>
                      {field.description ? `${field.description} ` : ''}
                      <span className="text-muted-foreground/80">
                        {source === 'default'
                          ? 'Default.'
                          : source === 'project'
                            ? 'Set for this project.'
                            : 'Set for all projects.'}
                      </span>
                      {Object.hasOwn(snapshot.overrides, key) && (
                        <Button
                          type="button"
                          disabled={busy}
                          variant="link"
                          size="sm"
                          className="ml-1 h-auto p-0 text-xs"
                          onClick={() => void save(key, null)}
                        >
                          Reset
                        </Button>
                      )}
                    </p>
                  </div>
                  {inline ? <div className="shrink-0">{control}</div> : null}
                </div>
                {inline ? null : <div className="mt-2">{control}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
type SettingField = NonNullable<InstalledPlugin['settings']>[string];

/** A number setting between two bounds, set with a slider. */
const slider = (field: SettingField) =>
  field.type === 'number' &&
  field.choices === undefined &&
  field.minimum !== undefined &&
  field.maximum !== undefined;

function SettingInput({
  id,
  describedBy,
  label,
  field,
  value,
  values = {},
  busy,
  save,
}: {
  id: string;
  describedBy: string | undefined;
  label: string;
  field: SettingField;
  value: PluginSettingValue;
  /** The plugin's other settings (the model a voice setting follows). */
  values?: Readonly<Record<string, PluginSettingValue>>;
  busy: boolean;
  save(value: PluginSettingValue): void;
}) {
  const [draft, setDraft] = useState(String(value));
  const className = 'w-full';
  if (field.picker?.kind === 'model')
    return (
      <ModelSettingPicker
        busy={busy}
        describedBy={describedBy}
        id={id}
        label={label}
        picker={field.picker}
        save={save}
        value={String(value)}
      />
    );
  if (field.picker?.kind === 'voice')
    return (
      <VoiceSettingPicker
        busy={busy}
        describedBy={describedBy}
        id={id}
        label={label}
        model={String(values[field.picker.model] ?? '')}
        save={save}
        value={String(value)}
      />
    );
  if (slider(field))
    return (
      <SettingSlider
        describedBy={describedBy}
        disabled={busy}
        id={id}
        label={label}
        maximum={field.maximum as number}
        minimum={field.minimum as number}
        save={save}
        value={Number(value)}
      />
    );
  if (field.choices)
    return (
      <NativeSelect
        aria-describedby={describedBy}
        id={id}
        disabled={busy}
        className="h-control-sm w-40 text-xs"
        value={JSON.stringify(value)}
        onChange={(event) => save(JSON.parse(event.target.value))}
      >
        {field.choices.map((choice) => (
          <option key={JSON.stringify(choice)} value={JSON.stringify(choice)}>
            {String(choice)}
          </option>
        ))}
      </NativeSelect>
    );
  if (field.type === 'boolean')
    return (
      <Switch
        id={id}
        aria-describedby={describedBy}
        aria-label={label}
        disabled={busy}
        checked={value === true}
        onCheckedChange={(checked) => save(checked)}
      />
    );
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (field.type !== 'number' || draft.trim())
          save(field.type === 'number' ? Number(draft) : draft);
      }}
      className="flex gap-2"
    >
      <Input
        id={id}
        aria-describedby={describedBy}
        className={className}
        disabled={busy}
        type={field.type === 'number' ? 'number' : 'text'}
        required={field.type === 'number'}
        step="any"
        min={field.minimum}
        max={field.maximum}
        maxLength={16000}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <PendingButton
        type="submit"
        pending={busy}
        disabled={draft === String(value)}
        variant="outline"
      >
        Save
      </PendingButton>
    </form>
  );
}

function SettingSlider({
  describedBy,
  disabled,
  id,
  label,
  maximum,
  minimum,
  save,
  value,
}: {
  describedBy: string | undefined;
  disabled: boolean;
  id: string;
  label: string;
  maximum: number;
  minimum: number;
  save(value: number): void;
  value: number;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  const current = draft ?? value;
  const step = (maximum - minimum) / 100;
  // As many decimals as the step has, so a fine range keeps its precision.
  const decimals = Math.min(10, Math.max(0, Math.ceil(-Math.log10(step)) + 1));
  const rounded = (value: number) =>
    Math.min(maximum, Math.max(minimum, Number(value.toFixed(decimals))));
  return (
    <div className="flex items-center gap-3">
      <Slider
        aria-describedby={describedBy}
        aria-label={label}
        className="w-40"
        disabled={disabled}
        id={id}
        max={maximum}
        min={minimum}
        onValueChange={([next]) => next !== undefined && setDraft(next)}
        onValueCommit={([next]) => {
          setDraft(null);
          if (next !== undefined) save(rounded(next));
        }}
        step={step}
        value={[current]}
      />
      <span className="w-10 text-right text-muted-foreground text-xs tabular-nums">
        {rounded(current)}
      </span>
    </div>
  );
}
